import { loginStore } from "@/store/loginStore";
import scanStore from "@/store/scanStore";
import { message } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  EnvironmentOutlined,
  FacebookOutlined,
  GlobalOutlined,
  InstagramOutlined,
  MailOutlined,
  PhoneOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";

const VerifyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = loginStore();
  const { template, verifyChip } = scanStore();
  const [isActivating, setIsActivating] = useState(false);

  // ─── Resolve config ──────────────────────────────────────────────
  const cfg = template?.config as any;
  console.log("cfg", template?.config);
  

  const companyName = cfg?.company?.name;
  const companySlogan = cfg?.company?.slogan;
  const companyEmail = cfg?.company?.email;
  const companyPhone = cfg?.company?.phone;
  const companyAddress = cfg?.company?.address;
  const companyWebsite = cfg?.company?.website;
  const companyLogo = cfg?.company?.logo;

  const facebook = cfg?.social?.facebook;
  const zalo = cfg?.social?.zalo;
  const instagram = cfg?.social?.instagram;
  const youtube = cfg?.social?.youtube;

  const banner = cfg?.banner;
  const bannerStyle = cfg?.bannerStyle || "full-width";
  const bannerHeight = cfg?.bannerHeight || 220;
  const bannerTitle = cfg?.bannerTitle;
  const bannerSubtitle = cfg?.bannerSubtitle;
  const content = cfg?.content;
  const gallery = cfg?.gallery;
  const galleryColumns = cfg?.galleryColumns || 2;
  const galleryBorderRadius = cfg?.galleryBorderRadius ?? 8;

  const primaryColor = cfg?.theme?.primaryColor || "#1a56db";
  const secondaryColor = cfg?.theme?.secondaryColor || "#f0f5ff";
  const accentColor = cfg?.theme?.accentColor || "#0e3fa1";
  const textColor = cfg?.theme?.textColor || "#1f2937";
  const bgColor = cfg?.theme?.backgroundColor || "#ffffff";
  const footerColor = cfg?.theme?.footerColor || "#111827";
  const headingFont = cfg?.theme?.headingFont || "'Montserrat', sans-serif";
  const bodyFont = cfg?.theme?.bodyFont || "system-ui, -apple-system, sans-serif";
  const baseFontSize = cfg?.theme?.baseFontSize || 14;
  const lineHeight = cfg?.theme?.lineHeight || 1.6;

  const px = cfg?.spacing?.paddingX ?? 16;
  const py = cfg?.spacing?.paddingY ?? 24;
  const sectionGap = cfg?.spacing?.sectionGap ?? 0;
  const borderRadius = cfg?.spacing?.borderRadius ?? 12;

  const showHeader = cfg?.layout?.showHeader ?? true;
  const showFooter = cfg?.layout?.showFooter ?? true;
  const showSocial = cfg?.layout?.showSocial ?? true;
  const showCTA = cfg?.layout?.showCTA ?? false;

  const cardShadow = cfg?.effects?.cardShadow ?? true;
  const sectionBorder = cfg?.effects?.sectionBorder ?? false;
  const scrollAnimation = cfg?.effects?.scrollAnimation ?? true;

  const ctaText = cfg?.cta?.text || "Liên hệ ngay";
  const ctaLink = cfg?.cta?.link;
  const ctaStyle = cfg?.cta?.style || "solid";

  // ─── Derived ─────────────────────────────────────────────────────
  const hasSocial = facebook || zalo || instagram || youtube;
  const hasContactInfo = companyEmail || companyPhone || companyAddress || companyWebsite;
  const galleryList: string[] = Array.isArray(gallery) ? gallery.filter(Boolean) : [];

  const footerTxt = useMemo(() => (luminance(footerColor) > 0.5 ? "#374151" : "#d1d5db"), [footerColor]);
  const footerMuted = useMemo(() => (luminance(footerColor) > 0.5 ? "#6b7280" : "#9ca3af"), [footerColor]);

  // ─── Helpers ─────────────────────────────────────────────────────
  const sec = (extra?: React.CSSProperties): React.CSSProperties => ({
    padding: `${py}px ${px}px`,
    marginBottom: sectionGap,
    borderBottom: sectionBorder ? `1px solid ${primaryColor}22` : "none",
    ...extra,
  });

  const ctaBtnStyle = useMemo((): React.CSSProperties => {
    const base: React.CSSProperties = {
      display: "block",
      width: "100%",
      padding: "14px 24px",
      fontSize: baseFontSize,
      fontWeight: 600,
      fontFamily: headingFont,
      textDecoration: "none",
      cursor: "pointer",
      textAlign: "center",
      letterSpacing: "0.02em",
      boxSizing: "border-box" as const,
      border: "none",
    };
    switch (ctaStyle) {
      case "outline":
        return {
          ...base,
          background: "transparent",
          color: primaryColor,
          border: `2px solid ${primaryColor}`,
          borderRadius,
        };
      case "gradient":
        return {
          ...base,
          background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`,
          color: "#fff",
          borderRadius,
          boxShadow: `0 4px 16px ${primaryColor}44`,
        };
      case "pill":
        return {
          ...base,
          background: primaryColor,
          color: "#fff",
          borderRadius: 999,
          boxShadow: `0 4px 16px ${primaryColor}33`,
        };
      default:
        return {
          ...base,
          background: primaryColor,
          color: "#fff",
          borderRadius,
          boxShadow: `0 4px 16px ${primaryColor}33`,
        };
    }
  }, [ctaStyle, primaryColor, accentColor, borderRadius, baseFontSize, headingFont]);

  const activateBtnStyle = useMemo(
    (): React.CSSProperties => ({
      display: "block",
      width: "100%",
      padding: "16px 24px",
      fontSize: baseFontSize * 1.1,
      fontWeight: 700,
      fontFamily: headingFont,
      textAlign: "center",
      letterSpacing: "0.02em",
      boxSizing: "border-box" as const,
      border: "none",
      borderRadius,
      cursor: "pointer",
      background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`,
      color: "#fff",
      boxShadow: `0 6px 20px ${primaryColor}40`,
      transition: "all 0.3s ease",
    }),
    [primaryColor, accentColor, borderRadius, baseFontSize, headingFont],
  );

  // Dynamic CSS for content headings, links, blockquote
  const dynamicCSS = useMemo(
    () => `
    .tp-root { color: ${textColor} !important; }
    .tp-content { color: ${textColor} !important; }
    .tp-content p, .tp-content li, .tp-content td, .tp-content span { color: ${textColor} !important; }
    .tp-content h1, .tp-content h2, .tp-content h3,
    .tp-content h4, .tp-content h5, .tp-content h6 { color: ${primaryColor}; }
    .tp-content a { color: ${primaryColor}; text-decoration-color: ${primaryColor}44; }
    .tp-content blockquote { border-left-color: ${primaryColor}; background: ${primaryColor}08; }
  `,
    [primaryColor, textColor],
  );

  // ─── Verify handler ──────────────────────────────────────────────
  const handleVerifyChip = async () => {
    if (!token) {
      const currentPath = location.pathname + location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
      return;
    }

    setIsActivating(true);
    try {
      await verifyChip();
      message.success("Kích hoạt thành công!");
      navigate("/");
    } catch {
      message.error("Kích hoạt thất bại, vui lòng thử lại!");
    } finally {
      setIsActivating(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <>
      <style>{dynamicCSS}</style>
      <section
        className={`tp-root${scrollAnimation ? " tp-animate" : ""}`}
        style={{
          fontFamily: bodyFont,
          fontSize: baseFontSize,
          lineHeight,
          color: textColor,
          background: bgColor,
          width: "100%",
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        {/* ── HEADER ── */}
        {showHeader && (companyName || companyLogo) && (
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              background: secondaryColor,
              borderBottom: `2px solid ${primaryColor}`,
              padding: `${py * 0.6}px ${px}px`,
            }}
          >
            {companyLogo && (
              <img
                src={companyLogo}
                alt="Logo"
                style={{ height: 40, width: "auto", objectFit: "contain", flexShrink: 0 }}
              />
            )}
            <div style={{ minWidth: 0, textAlign: "center" }}>
              {companyName && (
                <div
                  style={{
                    fontFamily: headingFont,
                    fontSize: baseFontSize * 1.2,
                    fontWeight: 700,
                    color: primaryColor,
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {companyName}
                </div>
              )}
              {companySlogan && (
                <div
                  style={{
                    fontSize: baseFontSize * 0.75,
                    color: accentColor,
                    fontWeight: 500,
                    marginTop: 2,
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {companySlogan}
                </div>
              )}
            </div>
          </header>
        )}

        {/* ── BANNER ── */}
        {banner && (
          <div
            className="tp-banner"
            style={{
              position: "relative",
              height: bannerHeight,
              overflow: "hidden",
              ...(bannerStyle === "contained" ? { margin: `0 ${px}px`, borderRadius } : {}),
              marginBottom: sectionGap,
            }}
          >
            <img
              src={banner}
              alt="Banner"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            {(bannerStyle === "gradient-overlay" || bannerTitle || bannerSubtitle) && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    bannerStyle === "gradient-overlay"
                      ? `linear-gradient(180deg, transparent 20%, ${primaryColor}cc 100%)`
                      : "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.6) 100%)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  padding: `${py}px ${px}px`,
                }}
              >
                {bannerTitle && (
                  <div
                    style={{
                      fontFamily: headingFont,
                      fontSize: baseFontSize * 1.8,
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1.2,
                      textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      marginBottom: bannerSubtitle ? 6 : 0,
                    }}
                  >
                    {bannerTitle}
                  </div>
                )}
                {bannerSubtitle && (
                  <div
                    style={{
                      fontSize: baseFontSize * 0.9,
                      color: "#ffffffcc",
                      fontWeight: 400,
                      textShadow: "0 1px 4px rgba(0,0,0,0.3)",
                    }}
                  >
                    {bannerSubtitle}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── CONTENT ── */}
        {content && (
          <div style={sec()}>
            <div
              className="ql-editor tp-content"
              style={{ padding: 0, fontFamily: bodyFont, fontSize: baseFontSize, lineHeight, color: textColor }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        )}

        {/* ── GALLERY ── */}
        {galleryList.length > 0 && (
          <div style={sec()}>
            <div
              style={{
                fontFamily: headingFont,
                fontSize: baseFontSize * 1.2,
                fontWeight: 700,
                color: primaryColor,
                marginBottom: 12,
              }}
            >
              Hình ảnh
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${galleryColumns}, 1fr)`, gap: 8 }}>
              {galleryList.map((src: string, idx: number) => (
                <div
                  key={idx}
                  className="tp-gallery-item"
                  style={{
                    overflow: "hidden",
                    borderRadius: galleryBorderRadius,
                    aspectRatio: "4/3",
                    ...(cardShadow ? { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } : {}),
                  }}
                >
                  <img
                    src={src}
                    alt={`Gallery ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CTA (nếu bật) ── */}
        {showCTA && ctaLink && (
          <div style={{ ...sec(), background: secondaryColor }}>
            <a href={ctaLink} style={ctaBtnStyle} target="_blank" rel="noopener noreferrer">
              {ctaText}
            </a>
          </div>
        )}

        {/* ── NÚT KÍCH HOẠT ── */}
        <div style={{ ...sec(), paddingTop: py * 0.5, paddingBottom: py * 0.5 }}>
          <button
            onClick={handleVerifyChip}
            disabled={isActivating}
            style={{
              ...activateBtnStyle,
              opacity: isActivating ? 0.7 : 1,
              position: "relative",
            }}
          >
            {isActivating ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <span
                  style={{
                    width: 18,
                    height: 18,
                    border: "2px solid rgba(255,255,255,0.3)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    animation: "tpSpin 0.6s linear infinite",
                    display: "inline-block",
                  }}
                />
                Đang kích hoạt...
              </span>
            ) : (
              "Kích hoạt"
            )}
          </button>
          <style>{`@keyframes tpSpin { to { transform: rotate(360deg); } }`}</style>
        </div>

        {/* ── FOOTER ── */}
        {showFooter && (hasContactInfo || (showSocial && hasSocial)) && (
          <footer style={{ background: footerColor, padding: `${py}px ${px}px`, color: footerTxt, marginTop: "auto" }}>
            {hasContactInfo && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  fontSize: baseFontSize * 0.82,
                  color: footerMuted,
                  marginBottom: showSocial && hasSocial ? 16 : 0,
                }}
              >
                {companyPhone && (
                  <a
                    href={`tel:${companyPhone}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: footerMuted,
                      textDecoration: "none",
                    }}
                  >
                    <PhoneOutlined /> {companyPhone}
                  </a>
                )}
                {companyEmail && (
                  <a
                    href={`mailto:${companyEmail}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: footerMuted,
                      textDecoration: "none",
                    }}
                  >
                    <MailOutlined /> {companyEmail}
                  </a>
                )}
                {companyAddress && (
                  <span style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <EnvironmentOutlined style={{ marginTop: 3, flexShrink: 0 }} /> {companyAddress}
                  </span>
                )}
                {companyWebsite && (
                  <a
                    href={companyWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      color: primaryColor,
                      textDecoration: "none",
                    }}
                  >
                    <GlobalOutlined /> {companyWebsite}
                  </a>
                )}
              </div>
            )}

            {showSocial && hasSocial && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {facebook && (
                  <SocialIcon href={facebook} color={primaryColor}>
                    <FacebookOutlined />
                  </SocialIcon>
                )}
                {instagram && (
                  <SocialIcon href={instagram} color={primaryColor}>
                    <InstagramOutlined />
                  </SocialIcon>
                )}
                {youtube && (
                  <SocialIcon href={youtube} color={primaryColor}>
                    <YoutubeOutlined />
                  </SocialIcon>
                )}
                {zalo && (
                  <SocialIcon href={zalo} color={primaryColor}>
                    <span style={{ fontWeight: 700, fontSize: "0.75em" }}>Z</span>
                  </SocialIcon>
                )}
              </div>
            )}

            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${footerTxt}22`,
                fontSize: baseFontSize * 0.72,
                color: footerMuted,
                textAlign: "center",
              }}
            >
              © {new Date().getFullYear()} {companyName || template?.name || "Company"}. All rights reserved.
            </div>
          </footer>
        )}
      </section>
    </>
  );
};

export default VerifyPage;

// ─── Sub-components ────────────────────────────────────────────────
function SocialIcon({ href, color, children }: { href: string; color: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: `${color}22`,
        color,
        fontSize: 18,
        transition: "all 0.3s ease",
        textDecoration: "none",
      }}
    >
      {children}
    </a>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────
function luminance(hex: string): number {
  if (!hex || typeof hex !== "string") return 0;
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
